import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { useCart } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useCart';
import { CartItemDialogFormFields } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/schemas/CartItemDialogFormSchema';
import { usePaymentMethods } from '@/components/modules/Application/components/PaymentMethod/_shared/hooks/usePaymentMethods';
import { useApplicationNavigation } from '@/components/modules/Application/hooks/useApplicationNavigation';
import { useAutoUpdateCartSummaryData } from '@/components/modules/Application/hooks/useAutoUpdateCartSummaryData';
import { useGetApplicationSteps } from '@/components/modules/Application/hooks/useGetApplicationSteps';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';

export const useAutoDetails = () => {
  const {
    state: { orderReference1 },
    actions: { updateApplicationContext },
  } = useApplicationContext();

  const { cart, setCartItem, submitCart } = useCart();
  const { updateStepsAndGoNext } = useApplicationNavigation();
  const { getSteps } = useGetApplicationSteps();
  const { arePaymentMethodsAvailable } = usePaymentMethods();

  useAutoUpdateCartSummaryData();

  const {
    watch,
    setValue,
    getValues,
    formState: { isValid },
  } = useFormContext<CartItemDialogFormFields>();

  const price = watch('item.price');
  const item = watch('item');

  const onSubmit = () => {
    submitCart();
    updateStepsAndGoNext(getSteps());
  };

  const doUpdateCart = () => {
    const currentItem = getValues('item');
    if (!currentItem?.['brand'] || !currentItem?.['model'] || !currentItem?.['version']) return;
    setCartItem({ ...currentItem }, 0);
  };

  const cartItem = useMemo(() => cart?.items.map((item) => item.toFormItem())[0], [cart]);

  useEffect(() => {
    if (cartItem) {
      setValue('item', cartItem);
    }
  }, []);

  useEffect(() => {
    const subscription = watch((_, { type, name }) => {
      const isProgrammaticClear =
        name === 'item.registrationDate' || name === 'item.plateNumber' || name === 'item.odometer';
      if (type === 'change' || isProgrammaticClear) {
        doUpdateCart();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // //updates price in cart when price in form changes
  useEffect(() => {
    if (!cartItem) return;
    setCartItem({ ...cartItem, price: item['price'] }, 0);
  }, [price]);

  useEffect(() => {
    if (orderReference1) {
      setValue('item.identificationNumber', orderReference1);
    }
  }, [orderReference1]);

  useEffect(() => {
    updateApplicationContext({ isCartFormValid: isValid });
  }, [isValid]);

  const paymentMethodsAvailable = useMemo(() => arePaymentMethodsAvailable(Number(price)).state, [price]);

  const isReferenceValid = !!orderReference1;

  return {
    onSubmit,
    isReferenceValid,
    paymentMethodsAvailable,
    price,
    cart,
  };
};
